import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { Activity, MediaAssetPublic, Waypoint, WaypointKind } from '@xingroute/schema'
import { computeMergedRows, type EditableWaypoint, type MergedRow } from '../lib/mergedWaypoints.js'
import type { TrackPointLite } from '../lib/snapToTrack.js'
import {
  WaypointRow,
  systemTypeLabel,
  userTypeLabel,
  systemDescLabel,
  resolveGlyphKind,
  type WaypointRowMode,
} from './WaypointRow.js'
import { WaypointKindPicker } from './editor/WaypointKindPicker.js'
import { WaypointAttachmentsPopover } from './editor/WaypointAttachmentsPopover.js'
import { useLocale } from '../locale/LocaleContext.js'

// Unified WaypointList — replaces both WaypointReadList and WaypointEditorList.
// `mode="view"`: read-only, click = pin/focus.
// `mode="edit"`: user rows editable, click = select, desc inline input.

export interface WaypointListProps {
  mode: WaypointRowMode
  waypoints: readonly Waypoint[]
  track?: readonly TrackPointLite[]
  // Selection state
  activeWaypointId?: string | null
  selectedWaypointIds?: readonly string[]
  showAllWaypoints?: boolean
  onWaypointClick?: (id: string) => void
  // Edit callbacks (only used in edit mode)
  onWaypointsChange?: (waypoints: EditableWaypoint[]) => void
  // Media
  mediaByWaypointId?: Map<string, MediaAssetPublic[]>
  mediaToken?: string | null
  onUploadWaypointMedia?: (waypointId: string, file: File) => Promise<void>
  onDeleteWaypointMedia?: (mediaId: string) => Promise<void>
}

export function WaypointList({
  mode,
  waypoints,
  track,
  activeWaypointId,
  selectedWaypointIds,
  showAllWaypoints = false,
  onWaypointClick,
  onWaypointsChange,
  mediaByWaypointId,
  mediaToken,
  onUploadWaypointMedia,
  onDeleteWaypointMedia,
}: WaypointListProps) {
  const { t } = useTranslation('common')
  const { locale } = useLocale()
  const rowRefs = useRef(new Map<string, HTMLDivElement>())
  const [pulseId, setPulseId] = useState<string | null>(null)
  const [kindPickerWpId, setKindPickerWpId] = useState<string | null>(null)
  const kindPickerAnchorRef = useRef<HTMLElement | null>(null)
  const [popoverWp, setPopoverWp] = useState<{ id: string; label: string } | null>(null)

  // Scroll to active waypoint
  useEffect(() => {
    if (!activeWaypointId) {
      setPulseId(null)
      return
    }
    const activeRow = rowRefs.current.get(activeWaypointId)
    if (typeof activeRow?.scrollIntoView === 'function') {
      activeRow.scrollIntoView({ block: 'nearest' })
    }
    setPulseId(activeWaypointId)
    const timer = window.setTimeout(() => setPulseId(null), 200)
    return () => window.clearTimeout(timer)
  }, [activeWaypointId])

  const mergedRows = useMemo<MergedRow[]>(
    () => computeMergedRows(waypoints as readonly EditableWaypoint[], track),
    [waypoints, track],
  )
  const visibleRows = mergedRows
  const selectedSet = useMemo(
    () => new Set<string>(selectedWaypointIds ?? []),
    [selectedWaypointIds],
  )

  function updateWaypoint(id: string, patch: Partial<EditableWaypoint>) {
    if (!onWaypointsChange) return
    const updated = (waypoints as EditableWaypoint[]).map((w) =>
      w.id === id ? { ...w, ...patch } : w,
    )
    onWaypointsChange(updated)
  }

  function deleteWaypoint(id: string) {
    if (!onWaypointsChange) return
    onWaypointsChange((waypoints as EditableWaypoint[]).filter((w) => w.id !== id))
  }

  if (visibleRows.length === 0) {
    return (
      <p className="text-xs text-[var(--xr-text-muted)]">
        {locale === 'zh' ? '尚无路点。' : 'No waypoints yet.'}
      </p>
    )
  }

  return (
    <>
      <ul className="divide-y divide-[var(--xr-border-default)]" data-testid="waypoint-list">
        {visibleRows.map((row) => {
          const isSystem = row.kind === 'system'
          const systemKind = row.kind === 'system' ? row.system.kind : undefined

          if (row.kind === 'system') {
            // View mode shows the descriptive label (起点/终点/最高点/最低点/中点)
            // as the row's type so the implementation "系统" tag never leaks into
            // public detail pages; edit mode keeps the "系统" tag + desc.
            const systemLabel = systemDescLabel(row.system.kind, t)
            return (
              <WaypointRow
                key={row.rowKey}
                mode={mode}
                isSystem
                typeLabel={mode === 'edit' ? systemTypeLabel(t) : systemLabel}
                desc={mode === 'edit' ? systemLabel : ''}
                glyphKind={resolveGlyphKind(true, row.system.kind, undefined, undefined, false)}
                selected={
                  showAllWaypoints || selectedSet.has(row.rowKey) || activeWaypointId === row.rowKey
                }
                onClick={() => onWaypointClick?.(row.rowKey)}
                rowRef={(node) => {
                  if (node) rowRefs.current.set(row.rowKey, node)
                  else rowRefs.current.delete(row.rowKey)
                }}
              />
            )
          }

          // User row
          const wp = row.waypoint
          const wpMedia = mediaByWaypointId?.get(wp.id) ?? []
          const rowId = wp.id
          const selected =
            showAllWaypoints ||
            selectedSet.has(rowId) ||
            selectedSet.has(wp.id) ||
            activeWaypointId === rowId ||
            activeWaypointId === wp.id ||
            pulseId === rowId ||
            pulseId === wp.id

          const typeLabel = userTypeLabel(wp.kind as WaypointKind | undefined, wp.name, t)

          const desc = locale === 'en' && wp.desc_en?.trim() ? wp.desc_en : (wp.desc ?? '')

          const glyphKind = resolveGlyphKind(
            isSystem,
            systemKind,
            wp.kind,
            wp.sym,
            wpMedia.length > 0,
          )

          return (
            <WaypointRow
              key={row.rowKey}
              mode={mode}
              isSystem={isSystem}
              typeLabel={typeLabel}
              desc={desc}
              glyphKind={glyphKind}
              selected={selected}
              attachmentsCount={wpMedia.length}
              onClick={() => onWaypointClick?.(rowId)}
              onDescChange={
                mode === 'edit' && activeWaypointId === wp.id
                  ? (newDesc) => updateWaypoint(wp.id, { desc: newDesc || undefined })
                  : undefined
              }
              onDelete={mode === 'edit' ? () => deleteWaypoint(wp.id) : undefined}
              onOpenKindPicker={
                mode === 'edit'
                  ? (anchor) => {
                      kindPickerAnchorRef.current = anchor
                      setKindPickerWpId(wp.id)
                    }
                  : undefined
              }
              onOpenAttachments={
                wpMedia.length > 0 ? () => setPopoverWp({ id: wp.id, label: typeLabel }) : undefined
              }
              rowRef={(node) => {
                if (node) {
                  rowRefs.current.set(wp.id, node)
                } else {
                  rowRefs.current.delete(wp.id)
                }
              }}
            />
          )
        })}
      </ul>

      {/* Kind picker popover (edit mode only) */}
      <WaypointKindPicker
        open={kindPickerWpId !== null}
        current={
          (waypoints as EditableWaypoint[]).find((w) => w.id === kindPickerWpId)?.kind as
            | WaypointKind
            | undefined
        }
        anchorRef={kindPickerAnchorRef}
        anchorKey={kindPickerWpId}
        onSelect={(kind) => {
          if (kindPickerWpId) updateWaypoint(kindPickerWpId, { kind })
        }}
        onClose={() => setKindPickerWpId(null)}
      />

      {/* Attachments popover */}
      <WaypointAttachmentsPopover
        open={popoverWp !== null}
        mode={mode === 'edit' ? 'edit' : 'read'}
        waypointLabel={popoverWp?.label ?? ''}
        items={popoverWp ? (mediaByWaypointId?.get(popoverWp.id) ?? []) : []}
        mediaToken={mediaToken}
        onClose={() => setPopoverWp(null)}
        onUpload={
          mode === 'edit' && popoverWp && onUploadWaypointMedia
            ? (file) => onUploadWaypointMedia(popoverWp.id, file)
            : undefined
        }
        onDelete={mode === 'edit' ? onDeleteWaypointMedia : undefined}
      />
    </>
  )
}
